import { useMutation } from "@apollo/client";
import {
  Button,
  ButtonGroup,
  ControlGroup,
  InputGroup,
  NumericInput,
} from "@blueprintjs/core";
import { Select2 } from "@blueprintjs/select";
import _, { isEqual } from "lodash";
import { Component, ReactNode, useEffect, useState } from "react";
import {
  CustomerOrder,
  CustomerOrderPosition,
  Distributor,
  UpdateCustomerOrderPositionInput,
} from "../../../../api/schema";
import { FlexBox } from "../../../../components/flex/Flex";
import { ValuesVariables } from "../../../../components/form/InputForm";
import { ItemRenderers } from "../../../../components/ItemRenderer";
import { UserLanguage } from "../../../../lang/lang";
import { useDistributors } from "../../../../store/Distributors";
import { _t } from "../../../../utils/pack";
import { SimpleButton } from "../../../Article/ui/SimpleButton";
import {
  DeleteCustomerOrderPositionMutation,
  UpdateCustomerOrderPositionMutation,
} from "../graphql";
import { CreateOrderPosition, onKeyDownPrice } from "./CreateOrderPosition";

export interface OrderPositionsProps {
  customer_order: CustomerOrder;
  on_update: () => void;
  on_change?: (v: boolean) => void;
}

export interface IOrderPositionProps {
  position: CustomerOrderPosition;
  on_update: () => void;
  on_change?: (v: boolean) => void;
}

interface OrderPositionChange {
  order_position: CustomerOrderPosition;
  changed: boolean;
}

interface OrderPositionsState {
  changes: OrderPositionChange[];
}

export class OrderPositions extends Component<
  OrderPositionsProps,
  OrderPositionsState
> {
  constructor(props: OrderPositionsProps) {
    super(props);
  }

  private hasChanges(): boolean {
    return _(this.state.changes).find((v) => v.changed) !== undefined;
  }

  componentDidMount(): void {}

  componentDidUpdate(
    prevProps: Readonly<OrderPositionsProps>,
    prevState: Readonly<OrderPositionsState>,
    snapshot?: any
  ): void {
    const has_prop_changes = !_.isEqual(this.props, prevProps);
    const has_state_changes = !_.isEqual(this.state, prevState);
    console.log({ has_prop_changes, has_state_changes });
  }

  render() {
    return (
      <FlexBox>
        <h2>{UserLanguage().pages.customerOrder.orderPositions}</h2>
        {_.map(this.props.customer_order.positions, (p) => (
          <OrderPosition
            key={"op_" + p.id}
            position={p}
            on_update={this.props.on_update}
            // on_change={}
            // changes={(v) => update_state(p, v)}
          />
        ))}
        <CreateOrderPosition
          customer_order={this.props.customer_order}
          on_create={this.props.on_update}
          // changes={(v) => setNewOrderPositionChanges(v)}
        />
        <OrderPositionsSum customer_order={this.props.customer_order} />
      </FlexBox>
    );
  }
}

export const OrderPosition: React.FC<IOrderPositionProps> = ({
  on_update,
  position,
  on_change: changes,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [positionText, setPositionText] = useState(position.text);
  const [positionOrderNumber, setPositionOrderNumber] = useState(
    position.order_number
  );
  const [positionQuantity, setPositionQuantity] = useState(position.quantity);
  const [positionPrice, setPositonPrice] = useState(position.price);
  const [positionDistributor, setPositionDistributor] =
    useState<Distributor | null>(position.distributor);

  const [edited, setEdited] = useState({
    text: false,
    quantity: false,
    price: false,
    distributor: false,
    order_number: false,
  });

  // console.log({ edited });

  const { distributors } = useDistributors();

  useEffect(() => {
    const text = !isEqual(position.text, positionText),
      price = !isEqual(position.price, positionPrice),
      quantity = !isEqual(position.quantity, positionQuantity),
      distributor = !isEqual(position.distributor?.id, positionDistributor?.id),
      order_number = !isEqual(position.order_number, positionOrderNumber);

    setEdited({
      text,
      price,
      quantity,
      distributor,
      order_number,
    });

    const _edited = distributor || price || quantity || text || order_number;
    console.log({ _edited, text, price, quantity, distributor, order_number });
    changes?.(_edited);
  }, [
    positionText,
    positionPrice,
    positionQuantity,
    positionDistributor,
    positionOrderNumber,
    position,
    changes,
  ]);

  const [update_position] = useMutation<
    CustomerOrderPosition,
    ValuesVariables<UpdateCustomerOrderPositionInput>
  >(UpdateCustomerOrderPositionMutation);

  const [delete_position] = useMutation<Boolean, { id: number }>(
    DeleteCustomerOrderPositionMutation
  );

  const save_position = () =>
    _t(setSubmitting(true))
      .then(() =>
        update_position({
          variables: {
            values: {
              id: position.id,
              price: positionPrice,
              quantity: positionQuantity,
              text: positionText,
              order_number: positionOrderNumber,
              distributor_id: positionDistributor?.id || null,
            },
          },
        })
      )
      .then((res) => {
        setSubmitting(false);
        if (res.data) on_update();
      })
      .catch((e) => {
        console.log("got error", e);
      });

  const remove_position = () =>
    _t(setSubmitting(true))
      .then(() =>
        delete_position({
          variables: { id: position.id },
        })
      )
      .then((res) => {
        setSubmitting(false);
        if (res.data) on_update();
      })
      .catch((e) => {
        console.log("got error", e);
      });

  return (
    <ControlGroup style={{ marginBottom: 10 }}>
      <NumericInput
        placeholder={UserLanguage().data.quantity_scale.quantity}
        onValueChange={(a) => setPositionQuantity(a)}
        value={positionQuantity}
        leftIcon="full-stacked-chart"
        allowNumericCharactersOnly={true}
        style={{ width: 100 }}
        intent={edited.quantity ? "primary" : "none"}
        min={1}
      />
      <InputGroup
        fill
        value={positionText}
        onChange={(e) => setPositionText(e.target.value)}
        intent={edited.text ? "primary" : "none"}
        placeholder={UserLanguage().data.customerOrder.text}
      />
      <Select2
        items={distributors}
        itemRenderer={ItemRenderers.Distributor_ShortName}
        onItemSelect={setPositionDistributor}
        activeItem={positionDistributor}
      >
        {positionDistributor ? (
          <ControlGroup>
            <Button
              text={positionDistributor.short_name}
              intent={edited.distributor ? "primary" : "none"}
            />
            <Button icon="cross" onClick={() => setPositionDistributor(null)} />
          </ControlGroup>
        ) : (
          <Button
            text={UserLanguage().actions.select(
              UserLanguage().data.distributor.label
            )}
          />
        )}
      </Select2>
      <InputGroup
        value={positionOrderNumber}
        style={{ width: 200 }}
        onChange={(e) => setPositionOrderNumber(e.target.value)}
        intent={edited.text ? "primary" : "none"}
        placeholder={UserLanguage().data.customerOrder.order_number}
      />
      <NumericInput
        placeholder={UserLanguage().data.quantity_scale.price}
        allowNumericCharactersOnly={false}
        buttonPosition="none"
        asyncControl
        style={{ width: 100 }}
        min={0}
        onBlur={(e) => {
          try {
            const n = parseFloat(
              (e.target as HTMLInputElement).value.split(",").join(".")
            );
            setPositonPrice(n);
          } catch {}
        }}
        onKeyDown={(e) => onKeyDownPrice(e, setPositonPrice)}
        value={positionPrice.toFixed(3)}
        leftIcon="euro"
        intent={edited.price ? "primary" : "none"}
      />
      <NumericInput
        disabled
        value={(positionPrice * positionQuantity).toFixed(3)}
        style={{ width: 100 }}
        buttonPosition="none"
        leftIcon="euro"
      />
      <ButtonGroup>
        <SimpleButton
          disabled={submitting}
          enabled={
            edited.price ||
            edited.quantity ||
            edited.text ||
            edited.distributor ||
            edited.order_number
          }
          intent="success"
          onClick={save_position}
          icon="edit"
        />
        <SimpleButton
          disabled={submitting}
          enabled
          intent="danger"
          onClick={remove_position}
          icon="cross"
        />
      </ButtonGroup>
    </ControlGroup>
  );
};

const OrderPositionsSum = (props: { customer_order: CustomerOrder }) => (
  <div
    style={{
      display: "flex",
      width: "100%",
      justifyContent: "flex-end",
      marginTop: "1em",
      fontSize: "1.25em",
    }}
  >
    <div style={{ fontWeight: 800, marginLeft: 15 }}>
      {UserLanguage().data.order_positions.sum}:{" "}
      {_(props.customer_order.positions)
        .map((p) => p.price * p.quantity)
        .sum()
        .toFixed(3)}
      €
    </div>
  </div>
);
